import { CommonModule } from '@angular/common';
import { Component, Inject, Input, NgModule, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { TranslateModule } from '@ngx-translate/core';
import { AuxiliaryDataFieldType } from '@vendasta/account-group';
import { RuleInterface } from '@vendasta/automata';
import {
  AuxiliaryDataModule,
  chooseHandler,
  FieldHandler,
  FieldHandlerValidator,
  FieldSchema,
  IntegerFieldHandler,
  ObjectType,
  RendererSelector,
  SchemaService,
} from '@vendasta/auxiliary-data-components';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { CustomFieldOperations } from '../operations';
import { TemplateableFieldHandler } from '../handlers/templateable-field-handler';
import { OperationFilterPipe } from '../operation-filter.pipe';

import { FieldTypeString } from '../modify-crm-data-panel/interface';

function convertAuxiliaryDataTypeToFieldTypeString(auxDataType: AuxiliaryDataFieldType): FieldTypeString {
  switch (auxDataType) {
    case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_CURRENCY:
      return 'currency';
    case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DATE:
      return 'date';
    case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_STRING:
      return 'string';
    case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DROPDOWN:
      return 'dropdown';
    case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_INTEGER:
      return 'integer';
    default:
      return '';
  }
}

function getValidatorsForType(auxDataType: AuxiliaryDataFieldType, selectedOperation: string): FieldHandlerValidator[] {
  const requiredValidator = {
    errorCode: 'required',
    errorMessage: 'AUTOMATIONS.EDITOR.ERRORS.REQUIRED',
    validatorFn: Validators.required,
  };
  const divisionByZeroValidator = {
    errorCode: 'divisionByZero',
    errorMessage: 'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.DIVIDE_BY_ZERO_ERROR',
    validatorFn: (control) => (control.value === 0 ? { divisionByZero: true } : null),
  };
  const currencyRequiredValidator: FieldHandlerValidator = {
    errorCode: 'currencyRequired',
    errorMessage: 'AUTOMATIONS.EDITOR.ERRORS.REQUIRED',
    validatorFn: (control) => {
      if (control.value === '' || control.value === null || isNaN(control.value)) {
        return { currencyRequired: true };
      }
      return null;
    },
  };

  switch (auxDataType) {
    case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_CURRENCY: {
      const currencyValidators = [currencyRequiredValidator];
      if (selectedOperation === 'DIVIDE') {
        currencyValidators.push(divisionByZeroValidator);
      }
      return currencyValidators;
    }
    case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DATE:
      return [requiredValidator];
    case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_STRING:
      return [requiredValidator];
    case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DROPDOWN:
      return [requiredValidator];
    case AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_INTEGER: {
      const integerValidators = [requiredValidator];
      if (selectedOperation === 'DIVIDE') {
        integerValidators.push(divisionByZeroValidator);
      }
      return integerValidators;
    }
    default:
      return [];
  }
}

function chooseFieldHandler(
  selectedFieldSchema: FieldSchema,
  selectedOperation: string,
  fieldValue: any,
): FieldHandler {
  if (
    selectedFieldSchema?.fieldType === AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_CURRENCY &&
    ['DIVIDE', 'MULTIPLY'].includes(selectedOperation)
  ) {
    return new IntegerFieldHandler(
      selectedFieldSchema.fieldId,
      selectedFieldSchema.fieldName,
      '',
      fieldValue,
      selectedFieldSchema.fieldDescription,
      getValidatorsForType(selectedFieldSchema.fieldType, selectedOperation),
    );
  } else if (
    selectedFieldSchema?.fieldType === AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_STRING &&
    selectedOperation !== 'CLEAR_VALUE'
  ) {
    return new TemplateableFieldHandler(
      selectedFieldSchema.fieldId,
      selectedFieldSchema.fieldName,
      '',
      fieldValue,
      selectedFieldSchema.fieldDescription,
      getValidatorsForType(selectedFieldSchema.fieldType, selectedOperation),
    );
  }

  return chooseHandler(
    selectedFieldSchema.fieldId,
    {
      description: selectedFieldSchema.fieldDescription,
      title: selectedFieldSchema.fieldName,
      type: convertAuxiliaryDataTypeToFieldTypeString(selectedFieldSchema.fieldType),
      dropdownOptions: selectedFieldSchema.dropdownOptions?.map((opt) => ({
        label: opt?.label || '',
        value: opt?.value || '',
      })),
      currencyCode: selectedFieldSchema.currencyCode,
      format: '',
    },
    fieldValue,
    false,
    getValidatorsForType(selectedFieldSchema.fieldType, selectedOperation),
  );
}

@Component({
  selector: 'automata-modify-auxiliary-data-panel',
  template: `
    <div class="content">
      <div class="description subtitle">
        {{ panelSubtitle | translate }}
      </div>
      <div class="control-container">
        <ng-container *ngIf="filteredFieldSchemas$ | async as fieldSchemas; else loading">
          <glxy-form-field required="true">
            <glxy-label>{{ 'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.SELECT_FIELD' | translate }}</glxy-label>
            <input type="text" matInput [formControl]="fieldSearch" [matAutocomplete]="auto" />
            <mat-autocomplete
              required
              #auto="matAutocomplete"
              [displayWith]="fieldName"
              ngDefaultControl
              (optionSelected)="onFieldSelected($event)"
            >
              <mat-option *ngFor="let field of fieldSchemas" [value]="field">
                {{ field.fieldName }}
              </mat-option>
            </mat-autocomplete>
            <glxy-error *ngIf="selectedField?.hasError('required') && selectedField.dirty">
              {{ 'AUTOMATIONS.EDITOR.ERRORS.REQUIRED' | translate }}
            </glxy-error>
            <glxy-error *ngIf="selectedField?.hasError('archivedField')">
              {{
                'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.ARCHIVED_FIELD_ERROR'
                  | translate: { fieldName: selectedField.value?.fieldName }
              }}
            </glxy-error>
          </glxy-form-field>
          <ng-container *ngIf="selectedFieldSchema$ | async as selectedFieldSchema">
            <glxy-form-field>
              <glxy-label>
                {{ 'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.SELECT_OPERATION' | translate }}
              </glxy-label>
              <mat-select [formControl]="selectedOperation" [disableOptionCentering]="true" ngDefaultControl>
                <mat-option
                  *ngFor="let operation of CustomFieldOperations | operationFilter: selectedFieldSchema"
                  [value]="operation.value"
                >
                  {{ operation.name | translate }}
                </mat-option>
              </mat-select>
              <glxy-error *ngIf="selectedOperation.hasError('required') && selectedOperation.dirty">
                {{ 'AUTOMATIONS.EDITOR.ERRORS.REQUIRED' | translate }}
              </glxy-error>
            </glxy-form-field>
          </ng-container>
          <ng-container *ngIf="selectedOperation.value && selectedOperation.value !== 'CLEAR_VALUE'">
            <aux-renderer-selector
              #valueField
              *ngIf="selectedFieldHandler$ | async as fieldHandler"
              [handler]="fieldHandler"
            ></aux-renderer-selector>
          </ng-container>
        </ng-container>
        <ng-template #loading>
          <div class="side-drawer-shimmer-container">
            <span class="selector stencil-shimmer"></span>
            <span class="selector stencil-shimmer"></span>
          </div>
        </ng-template>
      </div>
    </div>
  `,
  styleUrls: ['./modify-auxiliary-data-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatDialogModule,
    GalaxyWrapModule,
    AuxiliaryDataModule,
    OperationFilterPipe,
  ],
  providers: [SchemaService],
})
export class ModifyAuxiliaryDataPanelComponent extends CommonActionPanelComponent implements OnInit, OnDestroy {
  @Input() objectType: ObjectType;
  @Input() panelTitle: string;
  @Input() panelSubtitle: string;

  private readonly destroyed$$: Subject<void> = new Subject<void>();
  CustomFieldOperations = CustomFieldOperations;

  form: UntypedFormGroup;
  fieldSearch = new FormControl<string | FieldSchema>('');
  selectedField = new FormControl<FieldSchema>(null, [Validators.required]);
  selectedOperation = new UntypedFormControl('', [Validators.required]);
  @ViewChild('valueField', { static: false }) valueField: RendererSelector;

  fieldSchemas$: Observable<FieldSchema[]>;
  filteredFieldSchemas$: Observable<FieldSchema[]>;
  selectedFieldSchema$: Observable<FieldSchema>;
  selectedFieldHandler$: Observable<FieldHandler>;

  private firstLoad = true;

  constructor(
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) private readonly namespace$: Observable<string>,
    private readonly schemaService: SchemaService,
    protected readonly fb: UntypedFormBuilder,
  ) {
    super();
  }

  private isArchivedFieldSchema(fieldSchemas: FieldSchema[], archivedFieldSchema: FieldSchema): boolean {
    if (!archivedFieldSchema) {
      return false;
    }
    if (fieldSchemas.length === 0) {
      return true;
    }
    return fieldSchemas.findIndex((fieldSchema) => fieldSchema?.fieldId === archivedFieldSchema?.fieldId) === -1;
  }

  private setFieldSchemasObservables(fieldId: string, partnerId: string): void {
    const archivedFieldSchema$ = of(!!fieldId).pipe(
      switchMap((hasFieldId) => {
        if (!hasFieldId) {
          return of(null);
        }
        return this.schemaService
          .getFieldSchema(this.objectType, { fieldId: fieldId, partnerId: partnerId })
          .pipe(map((response) => response.fieldSchema));
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    const availableSchemas$ = this.namespace$.pipe(
      switchMap((partnerId) => this.schemaService.listFieldSchemas(this.objectType, partnerId, false, '', 100)),
      map((response) => response?.fieldSchemas || []),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    this.fieldSchemas$ = combineLatest([availableSchemas$, archivedFieldSchema$]).pipe(
      switchMap(([fieldSchemas, archivedFieldSchema]) => {
        if (this.isArchivedFieldSchema(fieldSchemas, archivedFieldSchema)) {
          return of([...fieldSchemas, archivedFieldSchema]);
        }
        return of(fieldSchemas);
      }),
    );
    this.filteredFieldSchemas$ = combineLatest([
      this.fieldSchemas$,
      this.fieldSearch.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([fieldSchemas, fieldSearch]) => {
        if (!fieldSearch || typeof fieldSearch !== 'string') {
          return fieldSchemas;
        }
        return fieldSchemas.filter((schema) => schema.fieldName.toLowerCase().includes(fieldSearch.toLowerCase()));
      }),
    );
  }

  private setSelectedFieldObservables(jsonData: any): void {
    this.selectedField.updateValueAndValidity();
    this.selectedField.valueChanges
      .pipe(
        startWith(jsonData?.field_id ? { fieldId: jsonData?.field_id } : null),
        map((fs) => fs?.fieldId),
        distinctUntilChanged(),
        takeUntil(this.destroyed$$),
      )
      .subscribe(() => {
        if (!this.firstLoad) {
          this.selectedOperation.setValue(null);
        }
      });
    const selectedFieldChanges$ = this.selectedField.valueChanges.pipe(
      startWith(jsonData?.field_id ? { fieldId: jsonData?.field_id } : null),
      map((fs) => fs?.fieldId),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    this.selectedFieldSchema$ = combineLatest([this.fieldSchemas$, selectedFieldChanges$]).pipe(
      map(([fieldSchemas, selectedField]) =>
        fieldSchemas?.find((fieldSchema) => fieldSchema.fieldId === selectedField),
      ),
      distinctUntilChanged((prev, curr) => prev?.fieldId === curr?.fieldId),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    const selectedOperationChanges$ = this.selectedOperation.valueChanges.pipe(
      startWith(jsonData?.operation || ''),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    this.selectedFieldHandler$ = combineLatest([this.selectedFieldSchema$, selectedOperationChanges$]).pipe(
      filter(() => !this.isProcessing),
      map(([selectedFieldSchema, selectedOperation]) => {
        let fieldValue: any = jsonData?.value || null;
        if (!this.firstLoad) {
          fieldValue = null;
        }
        return chooseFieldHandler(selectedFieldSchema, selectedOperation, fieldValue);
      }),
      tap(() => (this.firstLoad = false)),
    );
  }

  private addAsyncValidatorToFieldSelector(): void {
    this.selectedField.setAsyncValidators((control) => {
      return this.selectedFieldSchema$.pipe(
        take(1),
        switchMap((selectedFieldSchema) => {
          if (!!selectedFieldSchema?.archived && control.value?.fieldId === selectedFieldSchema.fieldId) {
            return of({ archivedField: true });
          }
          return of(null);
        }),
        tap((error) => {
          if (error?.archivedField) {
            this.selectedField.markAsTouched();
          }
        }),
      );
    });
    this.selectedField.updateValueAndValidity();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step ? JSON.parse(this.step.data) : null;

    this.form = this.fb.group({
      selectedField: this.selectedField,
      selectedOperation: this.selectedOperation,
    });
    this.namespace$
      .pipe(take(1))
      .subscribe((partnerId) => this.setFieldSchemasObservables(jsonData?.field_id, partnerId));
    this.setSelectedFieldObservables(jsonData);
    this.addAsyncValidatorToFieldSelector();

    this.fieldSchemas$.pipe(take(1)).subscribe((schemas) => {
      if (jsonData) {
        const foundSchema = schemas?.find((fieldSchema) => fieldSchema.fieldId === jsonData.field_id);
        this.selectedField.setValue(foundSchema);
        this.fieldSearch.setValue(foundSchema);
        this.selectedOperation.setValue(jsonData.operation);
      }
    });
    this.savingEnabled = true;
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }

  onFieldSelected(field: MatAutocompleteSelectedEvent): void {
    if (!field?.option?.value) {
      return;
    }
    this.selectedField.setValue(field.option.value);
    this.selectedField.updateValueAndValidity();
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    const controlsToValidate: (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] = [this.form];

    if (this.valueField?.handler?.formControl) {
      controlsToValidate.push(this.valueField.handler.formControl);
    }

    return controlsToValidate;
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    if (!this.form) {
      return jsonData;
    }

    jsonData.object_type = this.objectType;
    jsonData.field_id = this.selectedField.value?.fieldId;
    jsonData.operation = this.selectedOperation.value;
    if (jsonData.operation !== 'CLEAR_VALUE') {
      jsonData.value = this.valueField?.handler?.formControl?.value;
    } else {
      jsonData.value = null;
    }

    return jsonData;
  }

  getRules(): RuleInterface[] {
    return [];
  }

  isFormValid(): boolean {
    if (this.valueField?.handler?.formControl) {
      return this.form.valid && this.valueField.handler.formControl.valid;
    }

    return this.form.valid;
  }

  fieldName(field: FieldSchema): string {
    return field?.fieldName;
  }
}

/*
Angular Inline Module (pattern)
Basically a Single Component Angular Module, with the module in the same file.
See this blog:
  https://netbasal.com/aim-to-future-proof-your-standalone-angular-components-accb574d273f
And this set of schematics:
  @ngneat/aim
*/
@NgModule({
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyWrapModule,
    AuxiliaryDataModule,
    OperationFilterPipe,
    GalaxyFormFieldModule,
    ModifyAuxiliaryDataPanelComponent,
  ],
  providers: [SchemaService],
})
export class ChangeAuxiliaryDataComponentModule {}
